import type {
  ApiState,
  DiscordSettingState,
  EncoderConfig,
  EncoderStatus,
  GameClipConfigState,
  HostQrCodeParameters,
  PlaycastHostEdition,
  PlaycastWindowState,
  PlayerQrCodeParameters,
  StateStore,
  TwitchSettingsState,
} from '@playcastdotio/CoreTypes';
import { remoteHostList, machineNameState } from './stores/shared';
import { getStateStore, SharedStateStores } from './stores';
import {
  devFunctions,
  electronChangeShadowcamPreview,
  electronClipboardCopy,
  electronLogin,
  electronLogout,
  getIpcFunctions,
  setMicrophonePreviewVolume,
  setWindowState,
} from './modules/ipcInterface';
import { getRemoteControlInterface } from './modules/remoteClientInterface';
import { initLegacyIpcAnalyticsEvents } from './modules/events/legacyIpcAnalyticsEvents';
import { initializeRemoteEvents } from './modules/events';
import { HostSettingsPayload, Invitation, Profile, QueueSettingsBody } from '@playcastdotio/Messaging';
import { NotificationTypes } from '@playcastdotio/Settings';
import { PlaycastWebrtcPresetGroup } from '@playcastdotio/Peer';
import { sendMatchmakerHostConfig } from './modules/managers/matchmaker';
import config from '../config.json';

// HMR handling - dynamically import only in development to avoid Jest issues
if (import.meta.env.DEV) {
  import('./hmr').catch(() => {
    // HMR module not available or import.meta not supported
  });
}

export type DashboardFunctions = {
  startStream: () => void;
  stopStream: (reason?: string) => void;
  setStreamTitle: (title: string) => void;
  changeDisplay: (displayId: number) => void;
  setMicrophone: (deviceId: string) => void;
  setMicrophoneMuted: (enabled: boolean) => void;
  setMicrophoneVolume: (volume: number) => void;
  setDesktopAudio: (deviceId: string) => void;
  setDesktopAudioMuted: (enabled: boolean) => void;
  setDesktopAudioVolume: (volume: number) => void;
  setOutboundVideoDevice: (deviceId: string, windowCapturePid?: number) => void;
  setDisplaySurfing: (enabled: boolean) => void;
  kickPlayer: (playerId: string) => void;
  kickAll: () => void;
  setAllowedGamepadInput: (allowed: boolean, playerId?: string) => void;
  setAllowedKeyboardInput: (allowed: boolean, playerId?: string) => void;
  setAllowedMouseInput: (allowed: boolean, playerId?: string) => void;
  setAllowedTouchInput: (allowed: boolean, playerId?: string) => void;
  startUpdate: () => void;
  ignoreUpdate: () => void;
  setStaticImage: (imageBase64Blob: string, playerId?: string) => void;
  setQrCodeParameters: (qrCodeParameters: PlayerQrCodeParameters, playerId?: string) => void;
  setHostQrCodeParameters: (qrCodeParameters: HostQrCodeParameters) => void;
  fetchAudioSources: () => void;
  setDesktopAudioSource: (sourceId: string, playerId?: string) => void;
  setGamepadSharing: (gamepadIndex: number, enabled: boolean) => void;
  setAssignGamepadsAutomatically: (enabled: boolean) => void;
  pauseInput: (paused: boolean, playerId?: string) => void;
  acceptKnock: (knockId: string) => void;
  rejectKnock: (knockId: string) => void;
  reorderLobby: (pendingPlayers: string[]) => void;
  updateQueueSettings: (enabled: boolean, settings: QueueSettingsBody, instantConnect: boolean) => void;
  updateLobbySettings: (update: Invitation) => void;
  updateHostSettings: (presence: HostSettingsPayload) => void;
  updateInvitationDescription: (description: string) => void;
  setGamepadAssignmentEnabled: (playerId: string, gamepadIndex: number, enabled: boolean) => void;
  sendInvitationEmail: (email: string, message: string, invitationId?: string) => void;
  sendInvitationSms: (phone: string, message: string, invitationId?: string) => void;
  renameHost: (newName: string, remoteId: string) => void;
  setFilterGamepadButtons: (filtered: boolean) => void;
  setMaxGamepadAssignments: (maxAssignments: number) => void;
  setWebrtcPresetGroup: (presetGroup: PlaycastWebrtcPresetGroup) => void;
  setWebrtcShouldHandlePanic: (shouldHandlePanic: boolean) => void;
  showVideoPreview: (enabled?: boolean) => void;
  processLaunch: (launchId: string, argument: string) => void;
  processRestart: (argument: string) => void;
  processTerminate: (type: 'default' | 'pavilion') => void;
  processSetPolicy: (
    nonFocusInput: boolean,
    constrainMouse: boolean,
    allowToolbarAccess: boolean,
    forceProcessOnTop: boolean,
    launchTimeout: number,
    captureAudio: boolean,
    captureVideo: boolean,
    saveEnumeratedApps: boolean,
    onlyExitOnDisconnect: boolean,
    enableHostDominanceKick?: boolean,
  ) => void;
  processGetPolicy: () => void;
  processEnumRunning: () => void;
  processSelect: (processId: number) => void;
  processDisconnect: () => void;
  systemLogOff: (username: string) => void;
  overlayCreateImageOverlay: (
    overlayName: string,
    destName: string,
    imagePath: string,
    translate: number[],
    rotate: number[],
    scale: number[],
    visible: boolean,
  ) => void;
  createQrCodeOverlay: () => Promise<void>;
  overlayDeleteOverlay: (overlayName: string) => void;
  updateTwitchSettings: (settings: TwitchSettingsState) => void;
  updateDiscordSettings: (settings: DiscordSettingState) => void;
  setAllowPeerConnections: (allowPeerConnections: boolean) => void;
  updateQrCode: (base64QRCode: string) => void;
  updateShowStreamQrCode: (showStreamQrCode: boolean) => void;
  setEncoderConfig: (config: EncoderConfig) => void;
  updateGameClipConfig: (config: GameClipConfigState) => void;
  updateGameClipStatus: (status: EncoderStatus) => void;
  setAutomaticUpdatesEnabled: (enabled: boolean) => void;
  getAutomaticUpdatesEnabled: () => void;
  changeEdition: (edition: PlaycastHostEdition) => void;
  queryProfile: () => void;
  queryOwnerAliases: () => void;
  queryProfileInvitations: (cursor?: string, limit?: number, searchTerm?: string) => void;
  updateProfile: (profile: Profile) => void;
  profileCreateMatchmakerLobby: () => void;
  profileJoinMatchmakerLobby: () => void;
  profileLeaveMatchmakerLobby: () => void;
  profileStartMatchmakerLobby: (useSteamGuestLogin?: boolean, regionOverride?: string) => void;
  profileDestroyMatchmakerLobby: (selectedInvitationId?: string) => void;
  profileSelectInvitation: (invitationId?: string) => void;
  matchmakerKickPlayer: (playerId: string) => void;
  sendTwitchBot: (invitationId: string) => void;
  sendDiscordBot: (invitationId: string) => void;
  setWindowState: (state: PlaycastWindowState) => void;
  toggleEncoder: () => void;
  setAfkPreventionEnabled: (enabled: boolean) => void;
  setAfkPreventionSettings: (settings: { enabled: boolean; keyCode: number; intervalSeconds: number; keyDisplayName: string }) => void;
  testAfkPreventionKey: (keyCode: number, keyDisplayName: string) => void;
  // DisplayIO - ShadowDisplay Virtual Display Driver functions
  shadowDisplayAddDisplay: (
    width: number,
    height: number,
    refreshRate: number,
    deviceName: string,
    serialNumber: string,
    guid?: string,
  ) => void;
  shadowDisplayRemoveDisplay: (guid: string) => void;
  shadowDisplayRemoveAllDisplays: () => void;
  shadowDisplayGetDisplays: () => void;
  shadowDisplayGetDriverStatus: () => void;
  shadowDisplayPingDriver: () => void;
  shadowDisplaySetRenderAdapter: (adapterLuid: { lowPart: number; highPart: number }) => void;
  shadowDisplayGetWatchdog: () => void;
  shadowDisplayGetProtocolVersion: () => void;

  // Machine ID Attestation
  attestMachineId: (clerkUserId: string, clerkToken: string) => Promise<void>;
  checkAttestationStatus: () => { status: string; error?: string };
};

export type PlaycastFunctions = {
  dev?: {
    setApiRemoteUrl: (url: string) => void;
    reconnectMessageCaptureClient: () => void;
    showWebrtcInternals: () => void;
    showGpuInternals: () => void;
    launchBigPictureMode: () => void;
    stopPlayjectorPavilion: () => void;
    updateRegionOverride: (regionOverride: string | undefined) => void;
    apiState: StateStore<ApiState>;
  };
  electronFunctions?: {
    hostLogin: () => void;
    hostLogout: () => void;
    copyToClipboard: (text: string) => void;
    setWindowState: (state: PlaycastWindowState) => void;
    setMicrophonePreviewVolume: (volume: number) => void;
    killPlayjector: () => void;
    connectPlayjector: () => void;
    savePreferences: (phonePreferences: NotificationTypes, emailPreferences: NotificationTypes) => void;
    switchVideoPreviewCamera: (shadowcam: string) => void;
    getTempDir: () => Promise<string>;
    openFolderDialog: () => Promise<string>;
    forceMatchmakeable: () => void;
    setSteamInstall: (steamInstall: boolean) => void;
    setTouchInputEnabled: (enabled: boolean) => void;
  };
  remoteControlFunctions?: {
    savePreferences: (phonePreferences: NotificationTypes, emailPreferences: NotificationTypes) => void;
  };
  changeFunctionTarget: (target: string) => void;
  targetFunctions: DashboardFunctions;
  stateStores: SharedStateStores;
};

export const isElectron = globalThis.playcast?.isHost || false;

if (isElectron) {
  initLegacyIpcAnalyticsEvents();
  const initializeElectronAutoRefresh = () => {
    const stores = getStateStore('ipc');
    const hasConnectedPlayers = () => {
      // Check regular player state
      const regularPlayers = Object.keys(stores.playerState.currentState.players || {}).length > 0;

      // Also check if streaming in pavilion mode with an active session
      const isPavilionStreaming = stores.hostState.currentState.edition === 'pavilion' &&
                                   stores.hostState.currentState.status === 'streaming';

      // In pavilion mode, also check invitation state for connected players (cafe type only)
      const invitation = stores.invitationStateV2.currentState;
      const pavilionPlayers = isPavilionStreaming &&
                             invitation.type === 'cafe' &&
                             invitation.manifest?.players?.length > 0;

      return regularPlayers || pavilionPlayers;
    };

    let refreshDeferred = false;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const saveCurrentState = () => {
      const currentState = {
        // Stream and session settings
        streamTitle: stores.invitationStateV2.currentState.description,
        streamName: stores.invitationStateV2.currentState.name,

        // Display and video settings
        selectedDisplay: stores.displayState.currentState.info.selectedDisplayId,
        selectedVideoDevice: stores.videoState.currentState.selectedDevice,
        videoEnabled: stores.videoState.currentState.enabled,
        windowCapturePid: stores.videoState.currentState.windowCapturePid,
        staticImage: stores.staticImageState.currentState.staticImageBase64,
        showStaticImage: stores.showStaticImageState.currentState.showStaticImage,

        // Display state - preserve all display info
        displayState: {
          displays: stores.displayState.currentState.displays,
          sourceProperties: stores.displayState.currentState.sourceProperties,
          info: stores.displayState.currentState.info,
        },

        // Video state - preserve camera and shadowcam selections
        videoState: {
          selectedDevice: stores.videoState.currentState.selectedDevice,
          enabled: stores.videoState.currentState.enabled,
          windowCapturePid: stores.videoState.currentState.windowCapturePid,
          cameras: stores.videoState.currentState.cameras,
          shadowcams: stores.videoState.currentState.shadowcams
        },

        // Audio settings
        audioState: {
          desktopAudioMuted: stores.audioState.currentState.desktopAudioMuted,
          microphoneMuted: stores.audioState.currentState.microphoneMuted,
          microphoneVolume: stores.audioState.currentState.microphoneVolume,
          desktopAudioVolume: stores.audioState.currentState.desktopAudioVolume,
          // Preserve audio source selections
          microphoneSources: stores.audioState.currentState.microphoneSources,
          desktopAudioSources: stores.audioState.currentState.desktopAudioSources
        },

        // Gamepad settings
        gamepadAutoAssign: stores.gamepadState.currentState.autoAssign,
        maxGamepadAssignments: stores.gamepadState.currentState.maxAssignments,

        // Host rules and preferences
        showVideoPreview: stores.hostRuleState.currentState.showVideoPreview,
        videoPreviewCamera: stores.hostRuleState.currentState.videoPreviewCamera,
        hostAutoAccept: stores.hostRuleState.currentState.hostAutoAccept,
        enableGamepadFiltering: stores.hostRuleState.currentState.enableGamepadFiltering,

        // QR code settings
        qrCodeEnabled: stores.qrCodeState.currentState.defaultPlayerState.enabled,
        qrCodeScale: stores.qrCodeState.currentState.defaultPlayerState.scale,
        qrCodePosition: stores.qrCodeState.currentState.defaultPlayerState.position,

        // Twitch and Discord settings
        twitchSettings: stores.twitchSettingsState.currentState,
        discordSettings: stores.discordSettingsState.currentState,

        // Game clip settings
        gameClipConfig: stores.gameClipConfigState.currentState,

        // AFK prevention
        afkPrevention: stores.afkPreventionState.currentState,

        // Region override
        regionOverride: stores.regionOverrideState.currentState.regionOverride,

        // Window state
        windowState: stores.windowState.currentState,

        timestamp: Date.now()
      };
      localStorage.setItem('playcastStateBeforeRefresh', JSON.stringify(currentState));
    };

    const restoreState = () => {
      const savedState = localStorage.getItem('playcastStateBeforeRefresh');
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          // Only restore if saved within last 2 hours
          if (Date.now() - state.timestamp < 2 * 60 * 60 * 1000) {
            console.log('Restoring state after refresh...');

            // Set a flag to protect restored stream title
            localStorage.setItem('streamTitleRestored', 'true');

            // Restore stream settings
            if (state.streamTitle || state.streamName) {
              // Only restore if current state is empty or different
              const currentDescription = stores.invitationStateV2.currentState.description;
              const currentName = stores.invitationStateV2.currentState.name;

              if (currentDescription !== state.streamTitle || currentName !== state.streamName) {
                console.log('Restoring stream title from:', currentDescription, 'to:', state.streamTitle);
                stores.invitationStateV2.updateState({
                  ...stores.invitationStateV2.currentState,
                  description: state.streamTitle || '',
                  name: state.streamName || 'Unnamed session'
                });
              }
            }

            // Restore display and video settings
            if (state.displayState) {
              stores.displayState.updateState({
                ...stores.displayState.currentState,
                displays: state.displayState.displays || [],
                sourceProperties: state.displayState.sourceProperties || [],
                info: state.displayState.info || { selectedDisplayId: -1 }
              });
            }

            if (state.videoState) {
              stores.videoState.updateState({
                ...stores.videoState.currentState,
                selectedDevice: state.videoState.selectedDevice || 'display',
                enabled: state.videoState.enabled !== undefined ? state.videoState.enabled : true,
                windowCapturePid: state.videoState.windowCapturePid || undefined,
                cameras: state.videoState.cameras || [],
                shadowcams: state.videoState.shadowcams || []
              });
            }

            // Restore static image and show state
            if (state.staticImage) {
              stores.staticImageState.updateState({
                ...stores.staticImageState.currentState,
                staticImageBase64: state.staticImage
              });
            }

            if (state.showStaticImage !== undefined) {
              stores.showStaticImageState.updateState({
                showStaticImage: state.showStaticImage
              });
            }

            // Restore audio settings
            // stores.audioState.updateState({
            //   ...stores.audioState.currentState,
            //   desktopAudioMuted: state.desktopAudioMuted !== undefined ? state.desktopAudioMuted : false,
            //   microphoneMuted: state.microphoneMuted !== undefined ? state.microphoneMuted : true,
            //   microphoneVolume: state.microphoneVolume !== undefined ? state.microphoneVolume : 50,
            //   desktopAudioVolume: state.desktopAudioVolume !== undefined ? state.desktopAudioVolume : 50,
            //   desktopAudioSources: state.desktopAudioSources || stores.audioState.currentState.desktopAudioSources,
            //   microphoneSources: state.microphoneSources || stores.audioState.currentState.microphoneSources,
            // });

            // Restore gamepad settings
            stores.gamepadState.updateState({
              ...stores.gamepadState.currentState,
              autoAssign: state.gamepadAutoAssign !== undefined ? state.gamepadAutoAssign : true,
              maxAssignments: state.maxGamepadAssignments !== undefined ? state.maxGamepadAssignments : 4
            });

            // Restore host rules
            stores.hostRuleState.updateState({
              ...stores.hostRuleState.currentState,
              showVideoPreview: state.showVideoPreview !== undefined ? state.showVideoPreview : false,
              videoPreviewCamera: state.videoPreviewCamera || config.PREVIEW_SHADOWCAM,
              hostAutoAccept: state.hostAutoAccept !== undefined ? state.hostAutoAccept : true,
              enableGamepadFiltering: state.enableGamepadFiltering !== undefined ? state.enableGamepadFiltering : false
            });

            // Restore QR code settings
            stores.qrCodeState.updateState({
              ...stores.qrCodeState.currentState,
              defaultPlayerState: {
                ...stores.qrCodeState.currentState.defaultPlayerState,
                enabled: state.qrCodeEnabled !== undefined ? state.qrCodeEnabled : false,
                scale: state.qrCodeScale !== undefined ? state.qrCodeScale : 1,
                position: state.qrCodePosition || { x: 0, y: 0 }
              }
            });

            // Restore Twitch and Discord settings
            if (state.twitchSettings) {
              stores.twitchSettingsState.updateState(state.twitchSettings);
            }

            if (state.discordSettings) {
              stores.discordSettingsState.updateState(state.discordSettings);
            }

            // Restore game clip config
            if (state.gameClipConfig) {
              stores.gameClipConfigState.updateState(state.gameClipConfig);
            }

            // Restore AFK prevention
            if (state.afkPrevention) {
              stores.afkPreventionState.updateState(state.afkPrevention);
            }

            // Restore region override
            if (state.regionOverride) {
              stores.regionOverrideState.updateState({ regionOverride: state.regionOverride });
            }

            // Restore window state
            if (state.windowState) {
              stores.windowState.updateState(state.windowState);
            }

            console.log('State restoration completed');
          }
          localStorage.removeItem('playcastStateBeforeRefresh');
        } catch (error) {
          console.error('Failed to restore state:', error);
        }
      }
    };

    const scheduleRefresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      const refreshTime = new Date(Date.now() + config.AUTO_REFRESH_INTERVAL_MS);
      console.log(`[Auto-Refresh] Scheduled refresh in ${config.AUTO_REFRESH_INTERVAL_MS / 1000 / 60} minutes at ${refreshTime.toLocaleTimeString()}`);
      // refreshTimer = setTimeout(onRefreshDue, config.AUTO_REFRESH_INTERVAL_MS);
    };

    const onRefreshDue = () => {
      const playerCount = Object.keys(stores.playerState.currentState.players || {}).length;
      const invitation = stores.invitationStateV2.currentState;
      const pavilionPlayers = (invitation.type === 'cafe' && invitation.manifest?.players?.length) || 0;
      const isPavilion = stores.hostState.currentState.edition === 'pavilion';

      console.log('[Auto-Refresh] Refresh timer fired', {
        playerCount,
        pavilionPlayers,
        isPavilion,
        invitationType: invitation.type,
        streaming: stores.hostState.currentState.status === 'streaming',
        hasConnectedPlayers: hasConnectedPlayers()
      });

      if (hasConnectedPlayers()) {
        console.log('[Auto-Refresh] Deferring refresh - players still connected');
        refreshDeferred = true;
        return;
      }

      console.log('[Auto-Refresh] Refreshing application - no players connected');
      saveCurrentState();
      window.location.reload();
    };

    stores.playerState.addListener((state) => {
      const playersNow = Object.keys(state.players || {}).length;
      if (playersNow === 0 && refreshDeferred) {
        saveCurrentState();
        window.location.reload();
      }
    });

    scheduleRefresh();

    // Restore state after page loads
    setTimeout(restoreState, 3000);

    // Clear protection flag after a longer delay to allow normal operation
    setTimeout(() => {
      localStorage.removeItem('streamTitleRestored');
    }, 10000);
  };
  // setTimeout(() => {
  //   initializeElectronAutoRefresh();
  // }, 1000);
} else {
  initializeRemoteEvents();
}

let currentHostState = remoteHostList.currentState;
remoteHostList.addListener((state) => {
  currentHostState = state;
});
export const getFunctions = async (): Promise<PlaycastFunctions> => {
  const selectedHostId = remoteHostList.currentState.selectedHostId;
  const machineName = machineNameState.currentState;
  const remoteHostName = remoteHostList.currentState.hosts[selectedHostId]?.displayName;
  if (selectedHostId !== '') {
    console.log('[Index] Getting functions for', selectedHostId, machineName, remoteHostName);
  }

  const targetFunctions = isElectron ? getIpcFunctions() : await getRemoteControlInterface(selectedHostId);
  const stateStores = getStateStore(isElectron ? 'ipc' : selectedHostId);

  const changeTarget = (target: string) => {
    if (target !== currentHostState.selectedHostId) {
      console.log('[Index] Changing target to', target, JSON.stringify(currentHostState));
      remoteHostList.updateState({
        ...currentHostState,
        selectedHostId: target,
      });
    }
  };

  const res: PlaycastFunctions = {
    changeFunctionTarget: changeTarget,
    targetFunctions,
    stateStores,
  };

  if (isElectron) {
    res.electronFunctions = {
      hostLogin: () => {
        return electronLogin();
      },
      hostLogout: () => {
        return electronLogout();
      },
      copyToClipboard: (text) => {
        return electronClipboardCopy(text);
      },
      setWindowState: (state) => {
        return setWindowState(state);
      },
      setMicrophonePreviewVolume: (volume) => {
        setMicrophonePreviewVolume(volume);
      },
      killPlayjector: () => {
        window.playcast?.killPlayjector();
      },
      connectPlayjector: () => {
        window.playcast?.connectPlayjector();
      },
      savePreferences: (phonePreferences, emailPreferences) => {
        stateStores.preferenceState.updateState({
          phone: phonePreferences,
          email: emailPreferences,
        });
        console.error('[Electron] Save preferences not implemented');
      },
      switchVideoPreviewCamera: (shadowcam) => {
        electronChangeShadowcamPreview(shadowcam);
      },
      getTempDir: () => {
        return window.playcast?.getTempDir() || Promise.resolve('');
      },
      openFolderDialog: () => {
        return window.playcast?.openFolderDialog() || Promise.resolve('');
      },
      forceMatchmakeable: () => {
        stateStores.hostMatchmakingState.updateState({
          ...stateStores.hostMatchmakingState.currentState,
          matchmakeable: true,
        });
        sendMatchmakerHostConfig();
      },
      setSteamInstall: (steamInstall) => {
        stateStores.steamInstallState.updateState({
          ...stateStores.steamInstallState.currentState,
          steamInstall,
        });
        stateStores.hostState.updateState({
          ...stateStores.hostState.currentState,
          edition: steamInstall ? undefined : 'standard',
        });
      },
      setTouchInputEnabled: (enabled) => {
        stateStores.touchInputConfigState.updateState({
          enabled,
        });
        // Persist to localStorage
        try {
          localStorage.setItem('playcast.touchInputEnabled', JSON.stringify(enabled));
        } catch (err) {
          console.error('[TouchInput] Failed to persist setting:', err);
        }
      },
    };
  } else {
    res.remoteControlFunctions = {
      savePreferences: (phonePreferences, emailPreferences) => {
        stateStores.preferenceState.updateState({
          phone: phonePreferences,
          email: emailPreferences,
        });
        console.error('[RemoteControl] Save preferences not implemented');
      },
    };
  }
  const fns = devFunctions();
  if (fns) {
    res.dev = fns;
  }
  return res;
};

// const cleanup = () => {
//   destroyRemoteInterface();
//   destroyIpcInterface();
// };
